import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import {
  DEPARTMENTS, AGENT_ESCALATION, formatAmount, formatAmountCompact, deslugify, slugify, titleCase,
} from '../constants';
import ResolutionPanel from '../components/ResolutionPanel';
import AgentChat from '../components/AgentChat';
import KpiCard from '../components/ui/KpiCard';

// Level display config matching prototype
const LV_CFG: Record<string, { name: string; color: string }> = {
  L6: { name: 'Executive', color: 'var(--navy-800)' },
  L5: { name: 'Director', color: 'var(--navy-800)' },
  L4: { name: 'Manager', color: '#b45309' },
  L3: { name: 'Supervisor', color: '#6d28d9' },
  L2: { name: 'Analyst', color: '#059669' },
};

export default function AgentView() {
  const { department, agentId } = useParams<{ department: string; agentId: string }>();
  const navigate = useNavigate();
  const { personas, getExceptionsForAgent, loading, refreshData } = useApp();

  const deptName = deslugify(department || '');
  const deptConfig = DEPARTMENTS[deptName];
  const agent = personas.find((p: any) => p.id === agentId);
  const esc = AGENT_ESCALATION[agentId || ''] || { level: 'L2', limit: 50000 };
  const lv = LV_CFG[esc.level] || { name: esc.level, color: '#9CA3AF' };

  const [reviewingId, setReviewingId] = useState<number | null>(null);
  const [showChat, setShowChat] = useState(false);

  if (!deptConfig || !agent) {
    return (
      <div style={{ textAlign: 'center', padding: 80, color: 'var(--text-secondary)' }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>Agent Not Found</div>
        <button onClick={() => navigate('/')} style={{ padding: '10px 24px', background: 'var(--accent-subtle)', border: '1px solid var(--accent-border)', borderRadius: 8, color: 'var(--navy-700)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          Back to Org Chart
        </button>
      </div>
    );
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 80, color: 'var(--text-secondary)' }}>Loading...</div>;

  const { actionable, readOnly } = getExceptionsForAgent(agentId || '');
  const modeColor = agent.default_mode === 'auto' ? 'var(--green)' : agent.default_mode === 'human_decision' ? 'var(--red)' : 'var(--amber)';
  const modeLabel = agent.default_mode === 'auto' ? 'Autonomous' : agent.default_mode === 'human_decision' ? 'Human Decision' : 'Hybrid';

  return (
    <div>
      {/* Breadcrumb */}
      <div className="bc">
        <span className="bc-link" onClick={() => navigate('/')}>Organization</span>
        <span className="bc-sep">&#9658;</span>
        <span className="bc-link" onClick={() => navigate(`/department/${slugify(deptName)}`)}>{deptName}</span>
        <span className="bc-sep">&#9658;</span>
        <span className="bc-cur">{agent.name}</span>
      </div>

      {/* Agent Header Card */}
      <div className="card" style={{ padding: '20px 24px', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
          {/* Avatar */}
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            background: 'var(--bg-elevated)', border: `2px solid ${lv.color}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1rem', fontWeight: 600,
          }}>
            {agent.name.charAt(0)}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{agent.name}</div>
            <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{agent.title || agent.department}</div>
          </div>
          {/* Level badge */}
          <span style={{
            fontSize: '0.7rem', fontWeight: 600, padding: '3px 10px', borderRadius: 4,
            background: 'var(--accent-subtle)', color: 'var(--navy-800)',
          }}>
            {esc.level} {lv.name}
          </span>
          {/* Mode badge */}
          <span style={{
            fontSize: '0.7rem', fontWeight: 600, padding: '3px 10px', borderRadius: 4,
            background: `${modeColor}15`.replace('var(', 'rgba(').replace(')', ',0.15)'),
            color: modeColor,
          }}>
            {modeLabel}
          </span>
        </div>

        {/* 5 KPI boxes inline */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14 }}>
          <KpiCard label="Approval Limit" value={formatAmountCompact(esc.limit)} valueColor="var(--red)" compact />
          <KpiCard label="Skills" value={agent.skills_count || 0} valueColor="var(--accent)" compact />
          <KpiCard label="Actionable Tasks" value={actionable.length} valueColor="var(--amber)" compact />
          <KpiCard label="Review & Escalate" value={readOnly.length} valueColor="var(--text-secondary)" compact />
          <KpiCard label="Department" value={deptName} valueColor="var(--accent)" compact />
        </div>
      </div>

      {/* Main: Task Queue + Resolution/Chat */}
      <div style={{ display: 'grid', gridTemplateColumns: reviewingId || showChat ? '1fr 440px' : '1fr', gap: 20 }}>
        {/* Left: Task Queue */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: '1rem', fontWeight: 600 }}>Task Queue</div>
            <button
              onClick={() => { setShowChat(!showChat); setReviewingId(null); }}
              style={{
                padding: '7px 18px', fontSize: '0.78rem', fontWeight: 600,
                background: showChat ? 'var(--accent-subtle)' : 'var(--bg-elevated)',
                border: `1px solid ${showChat ? 'var(--accent-border)' : 'var(--border-default)'}`,
                borderRadius: 8, color: showChat ? 'var(--accent)' : 'var(--text-primary)', cursor: 'pointer',
              }}
            >
              {showChat ? 'Close Chat' : 'Chat with Agent'}
            </button>
          </div>

          {/* Actionable tasks */}
          {actionable.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div className="stitle">ACTIONABLE ({actionable.length})</div>
              {actionable.map((exc: any) => (
                <TaskCard
                  key={exc.id}
                  exc={exc}
                  actionable
                  isReviewing={reviewingId === exc.id}
                  onReview={() => { setReviewingId(reviewingId === exc.id ? null : exc.id); setShowChat(false); }}
                />
              ))}
            </div>
          )}

          {/* Read-only tasks */}
          {readOnly.length > 0 && (
            <div>
              <div className="stitle">ABOVE LIMIT — REVIEW & ESCALATE ({readOnly.length})</div>
              {readOnly.map((exc: any) => (
                <TaskCard key={exc.id} exc={exc} actionable={false} isReviewing={reviewingId === exc.id} onReview={() => { setReviewingId(reviewingId === exc.id ? null : exc.id); setShowChat(false); }} />
              ))}
            </div>
          )}

          {actionable.length === 0 && readOnly.length === 0 && (
            <div className="card" style={{ padding: 48, textAlign: 'center' }}>
              <div style={{ fontSize: '0.88rem', fontWeight: 600, marginBottom: 6 }}>No tasks in queue</div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>This agent has no assigned exceptions</div>
            </div>
          )}
        </div>

        {/* Right: Resolution Panel or Chat */}
        {reviewingId && (
          <ResolutionPanel
            exceptionId={reviewingId}
            onClose={() => setReviewingId(null)}
            onResolved={() => { refreshData(); }}
          />
        )}
        {showChat && !reviewingId && (
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 8, height: 500, overflow: 'hidden' }}>
            <AgentChat agentId={agentId || ''} agentName={agent.name} />
          </div>
        )}
      </div>
    </div>
  );
}

function TaskCard({ exc, actionable, isReviewing, onReview }: { exc: any; actionable: boolean; isReviewing: boolean; onReview: () => void }) {
  const typeLabel = titleCase(exc.exception_type) || 'exception';
  const severity = exc.severity || 'medium';
  const statusLower = (exc.status || '').toLowerCase();
  const isFlagged = statusLower === 'flagged';
  const isEscalated = statusLower === 'escalated';

  return (
    <div
      className="card"
      style={{
        padding: '12px 16px', marginBottom: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        cursor: 'pointer',
        opacity: actionable ? 1 : 0.7,
        borderColor: isReviewing ? 'var(--accent)' : undefined,
        background: isReviewing ? 'var(--accent-subtle)' : undefined,
      }}
      onClick={onReview}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--text-tertiary)', flexShrink: 0 }} />
        <span className="mono" style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', flexShrink: 0 }}>#{exc.id}</span>
        <span style={{ fontSize: '0.82rem', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {exc.title || typeLabel}
          {exc.amount ? ` — ${formatAmount(exc.amount)}` : ''}
        </span>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 8 }}>
          <span style={{ fontSize: '0.65rem', padding: '2px 8px', borderRadius: 4, fontWeight: 600, background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>
            {typeLabel}
          </span>
          <span style={{ fontSize: '0.65rem', padding: '2px 8px', borderRadius: 4, fontWeight: 600, background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>
            {severity}
          </span>
          {(isFlagged || isEscalated) && (
            <span style={{
              fontSize: '0.65rem', padding: '2px 8px', borderRadius: 4, fontWeight: 600,
              background: isEscalated ? '#fef2f2' : '#fffbeb',
              color: isEscalated ? 'var(--red)' : 'var(--amber)',
            }}>
              {isEscalated ? 'escalated' : 'flagged'}
            </span>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0, marginLeft: 16 }}>
        <span className="mono" style={{ fontSize: '0.82rem', fontWeight: 600 }}>{formatAmount(exc.amount || 0)}</span>
        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--accent)', cursor: 'pointer' }}>
          {isReviewing ? 'CLOSE' : 'REVIEW'}
        </span>
      </div>
    </div>
  );
}
